import type { AdminUserFilters } from '@/admin/adminApi';

/**
 * Vues et filtres de la liste des comptes (BO-15), en fonctions pures.
 *
 * Les vues sont **ecrites ici, dans le code** : ce ne sont pas des vues
 * enregistrees (hors perimetre, aucune table) mais des filtres nommes. Les
 * onglets n'affichent aucun compteur : le serveur n'en rend pas par vue, et un
 * compteur calcule cote client ne porterait que sur la page lue.
 *
 * L'etat de la liste vit dans l'adresse (`?state=suspended&page=2`), pour que le
 * retour depuis une fiche retrouve la liste filtree. Une valeur inconnue dans
 * l'adresse retombe sur la valeur par defaut **de l'ecran** (une adresse se
 * modifie a la main) ; c'est le serveur, lui, qui refuse une valeur inconnue.
 */

export type AccountView = 'all' | 'active_7d' | 'unverified' | 'administration' | 'suspended' | 'anonymized';

export const ACCOUNT_VIEWS: readonly { readonly value: AccountView; readonly label: string }[] = [
  { value: 'all', label: 'Tous' },
  { value: 'active_7d', label: 'Actifs · 7 j' },
  { value: 'unverified', label: 'Non vérifiés' },
  { value: 'administration', label: 'Administration' },
  { value: 'suspended', label: 'Suspendus' },
  { value: 'anonymized', label: 'Anonymisés' },
];

/** Valeur de l'option « pas de filtre » des listes deroulantes (jamais envoyee). */
export const ANY = 'any';

export const ROLE_FILTERS: readonly { readonly value: string; readonly label: string }[] = [
  { value: ANY, label: 'Tous les rôles' },
  { value: 'support', label: 'Support' },
  { value: 'admin', label: 'Administrateur' },
];

export const BABIES_FILTERS: readonly { readonly value: string; readonly label: string }[] = [
  { value: ANY, label: 'Avec ou sans profil' },
  { value: 'some', label: 'Au moins un profil' },
  { value: 'none', label: 'Aucun profil' },
];

export interface AccountListQuery {
  /** Fragment d'adresse, deja nettoye. */
  readonly search: string;
  readonly view: AccountView;
  /** `''` : pas de filtre. */
  readonly role: string;
  /** Jours `AAAA-MM-JJ`, `''` : pas de borne. */
  readonly createdFrom: string;
  readonly createdTo: string;
  readonly babies: string;
  readonly page: number;
}

export const EMPTY_QUERY: AccountListQuery = {
  search: '',
  view: 'all',
  role: '',
  createdFrom: '',
  createdTo: '',
  babies: '',
  page: 1,
};

const oneOf = (value: string | null, allowed: readonly string[], fallback: string): string =>
  value !== null && allowed.includes(value) ? value : fallback;

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Jour **complet et plausible**, seul a partir vers le serveur. Un champ de date
 * saisi au clavier emet une valeur valide a chaque segment (annee « 0002 », « 0020 »,
 * « 0202 » avant « 2026 ») : chacune ferait une requete et une ligne `admin.access`.
 */
export const isCompleteDay = (value: string): boolean => /^(19|20)\d{2}-\d{2}-\d{2}$/.test(value);

export const parseAccountQuery = (params: URLSearchParams): AccountListQuery => {
  const page = Number.parseInt(params.get('page') ?? '', 10);
  const day = (name: string): string => {
    const value = params.get(name) ?? '';

    return DAY.test(value) ? value : '';
  };

  return {
    search: (params.get('search') ?? '').trim(),
    view: oneOf(params.get('state'), ACCOUNT_VIEWS.map((view) => view.value), 'all') as AccountView,
    role: oneOf(params.get('role'), ['support', 'admin'], ''),
    createdFrom: day('createdFrom'),
    createdTo: day('createdTo'),
    babies: oneOf(params.get('babies'), ['some', 'none'], ''),
    page: Number.isInteger(page) && page >= 1 ? page : 1,
  };
};

/** Adresse de la liste : seules les valeurs differentes du defaut y figurent. */
export const serializeAccountQuery = (query: AccountListQuery): URLSearchParams => {
  const params = new URLSearchParams();

  if (query.search !== '') params.set('search', query.search);
  if (query.view !== 'all') params.set('state', query.view);
  if (query.role !== '') params.set('role', query.role);
  if (query.createdFrom !== '') params.set('createdFrom', query.createdFrom);
  if (query.createdTo !== '') params.set('createdTo', query.createdTo);
  if (query.babies !== '') params.set('babies', query.babies);
  if (query.page > 1) params.set('page', String(query.page));

  return params;
};

/** Filtres envoyes au serveur ; `all` n'est pas un filtre. */
export const serverFilters = (query: AccountListQuery): AdminUserFilters => ({
  state: query.view === 'all' ? '' : query.view,
  role: query.role,
  createdFrom: query.createdFrom,
  createdTo: query.createdTo,
  babies: query.babies,
});

/** Au moins une restriction (recherche, vue ou filtre) : « Effacer les filtres » a un sens. */
export const hasActiveCriteria = (query: AccountListQuery): boolean =>
  query.search !== '' ||
  query.view !== 'all' ||
  query.role !== '' ||
  query.createdFrom !== '' ||
  query.createdTo !== '' ||
  query.babies !== '';

/** Bornes inversees : le serveur les refuserait (`422`), l'ecran le dit avant d'appeler. */
export const invertedDays = (query: AccountListQuery): boolean =>
  query.createdFrom !== '' && query.createdTo !== '' && query.createdFrom > query.createdTo;

/** Cle de lecture : change quand, et seulement quand, la requete change. */
export const queryKey = (query: AccountListQuery): string => serializeAccountQuery(query).toString();
